
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref,
  PropType
} from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast } from "../../modules/message";
import {
  ContratoTransportadorPayload,
  useContratoTransportadorService
} from "../../modules/business/orcamento/contratoTransportadorBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import EntitySelectorCliente from "../cliente/EntitySelectorCliente.vue";
import EntitySelectorOrgaoAet from "../common/EntitySelectorOrgaoAet.vue";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import { ClientePayload } from "../../modules/business/cliente/clienteBusiness";

interface ContratoCadastroState {
  entity: ContratoTransportadorPayload & { cliente?: ClientePayload | null };
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
}

export default defineComponent({
  setup(props, { emit }) {
    const state = reactive<ContratoCadastroState>({
      entity: useContratoTransportadorService().safeContrato(),
      savedEntity: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: []
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        !!(
          stateLoading.value ||
          (props.idContratoTransportador &&
            !amIAllowed("edit", state.entity.permissionInfo))
        )
    );

    const tiposSolicitacao = computed(() =>
      useTipoLicencaOrgaoService().getTiposLicencaOrgao()
    );

    const transportadorSelecionado = computed<TransportadorPayload | undefined>(
      () => {
        if (!state.entity.idTransportador) {
          return undefined;
        }
        return {
          idTransportador: state.entity.idTransportador,
          nome: state.entity.nomeTransportador ?? null,
          cpfCnpj: state.entity.cpfCnpjTransportador ?? null,
          municipio: {},
          contratoSocial: {},
          procuracao: {},
          usuario: {},
          transportadorOrgaos: []
        } as TransportadorPayload;
      }
    );

    const setEntity = (entity: ContratoTransportadorPayload) => {
      state.entity = useContratoTransportadorService().safeContrato(entity);
    };

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const confirmSelectionCliente = (c: ClientePayload) => {
      if (c) {
        state.entity.cliente = c;
      }
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      if (t && t.idTransportador) {
        state.entity.idTransportador = t.idTransportador;
        state.entity.nomeTransportador = t.nome ?? null;
        state.entity.cpfCnpjTransportador = t.cpfCnpj ?? null;
      } else {
        state.entity.idTransportador = null;
        state.entity.nomeTransportador = null;
        state.entity.cpfCnpjTransportador = null;
      }
    };

    const afterSetOrgao = (idOrgao: number | null) => {
      state.entity.idOrgao = idOrgao;
    };

    const submitSave = () => {
      const idProps = props.idContratoTransportador ?? 0;
      const isInsert = !idProps;
      const {
        saveNewContrato,
        updateContrato,
        loading,
        data
      } = useContratoTransportadorService();
      state.loadings.push(loading);
      (isInsert
        ? saveNewContrato(state.entity)
        : updateContrato(idProps, state.entity)
      )
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.savedEntity = true;
          useToast().success("Contrato salvo com sucesso");
          if (isInsert) {
            emit("after-save-new-entity", data.value.idContratoTransportador);
          }
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    onMounted(async () => {
      if (props.idContratoTransportador) {
        const {
          getContrato,
          loading,
          data
        } = useContratoTransportadorService();
        state.loadings.push(loading);
        getContrato(props.idContratoTransportador)
          .then(() => {
            setEntity(data.value);
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            state.stateReady = true;
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            closeScreen();
          });
      } else {
        if (props.cliente) {
          state.entity.cliente = props.cliente;
        }
        state.stateReady = true;
      }
    });

    return {
      submitSave,
      closeScreen,
      amIAllowed,
      confirmSelectionCliente,
      confirmSelectionTransportador,
      afterSetOrgao,
      tiposSolicitacao,
      transportadorSelecionado,
      stateLoading,
      stateBlocked,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorCliente,
    EntitySelectorOrgaoAet,
    EntitySelectorTransportador
  },
  props: {
    idContratoTransportador: Number,
    cliente: Object as PropType<ClientePayload>,
    parentLoading: Boolean
  }
});
